import React from 'react';
import { TreeEntry, TreePath } from './types';
import {
  divider_css,
  icon_css,
  icon_open_css,
  icon_slot_css,
  icon_slot_toggle_css,
  tree_action_css,
  tree_button_css,
  tree_button_selected_css,
  tree_checkable_button_css,
  tree_text_css,
} from './styles';
import { IconArrowTriangleRight, IconLoadingDonuts } from '../icons/generated';
import { useTreeContext } from './context';
import { semantic_colors } from '../../foundation';
import { Checkbox } from '../checkbox/Checkbox';

export interface TreeItemProps {
  item: TreeEntry;
  path: TreePath;
  index: number;
  siblingCount: number;
  children?: React.ReactNode;
}

export const TreeItem: React.FC<TreeItemProps> = ({ item, path, index, siblingCount, children }) => {
  const {
    expandedIds,
    selectedIds,
    checkable,
    shouldExpandOnSelect,
    onSelect,
    onExpand,
    onCheck,
    getCheckState,
    getRoverItemProps,
  } = useTreeContext();
  const canExpand = item.hasChildren || !!children;
  const isExpanded = canExpand && expandedIds.has(item.id);
  const isSelected = selectedIds.has(item.id);
  const { checked: isChecked, indeterminate } = getCheckState(item);
  // 확장/접힘이 행 선택과 분리된 아이콘 전용 컨트롤인지. (키보드 확장은 방향키(useTreeRover)가 담당한다)
  const isStandaloneExpandToggle = !shouldExpandOnSelect && canExpand;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onCheck(item, !isChecked);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const target = event.target;
    if (isStandaloneExpandToggle && target instanceof Element && target.closest('[data-tree-expand-toggle]')) {
      onExpand(item, path);
      return;
    }

    onSelect(item, path);
  };

  return (
    <li
      role='treeitem'
      aria-expanded={isExpanded}
      aria-selected={isSelected}
      aria-setsize={siblingCount}
      aria-posinset={index + 1}
      aria-level={path.length + 1}
      aria-checked={checkable ? (indeterminate ? 'mixed' : isChecked) : undefined}
    >
      <button
        css={[
          tree_button_css,
          checkable && tree_checkable_button_css,
          isSelected && !checkable && tree_button_selected_css,
        ]}
        type='button'
        onClick={handleClick}
        {...getRoverItemProps(item, path)}
      >
        {path.map((_, index) => (
          <span css={divider_css} key={index} />
        ))}
        {/* 확장/접힘 분리 시 아이콘은 마우스 전용 컨트롤(키보드는 방향키가 담당)이라 AT에서 숨긴다. */}
        <span
          css={[icon_slot_css, isStandaloneExpandToggle && icon_slot_toggle_css]}
          aria-hidden={isStandaloneExpandToggle || undefined}
          data-tree-expand-toggle={isStandaloneExpandToggle || undefined}
        >
          {canExpand && !item.loading && (
            <IconArrowTriangleRight size={16} css={[icon_css, isExpanded && icon_open_css]} />
          )}
          {item.loading && <IconLoadingDonuts color={semantic_colors.content.secondary} size={16} />}
        </span>
        {checkable ? (
          <Checkbox
            key={`${item.id}-${isChecked}`}
            checked={isChecked}
            indeterminate={indeterminate}
            onChange={handleCheckboxChange}
            size='medium'
            unstableFocusVisible={false}
          >
            <span css={tree_text_css}>{item.displayLabel ?? item.label}</span>
          </Checkbox>
        ) : (
          <span css={tree_text_css}>{item.displayLabel ?? item.label}</span>
        )}

        {item.action != null && (
          <span css={tree_action_css}>
            {item.actionIcon}
            <span>{item.action}</span>
          </span>
        )}
      </button>
      {isExpanded && children}
    </li>
  );
};
